import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, fmtErr, img, money } from "@/lib/api";
import { track } from "@/lib/track";
import { useAuth } from "@/context/AuthContext";

const inputCls = "mt-2 w-full rounded-sm border border-[#3b1d63] bg-[#07030c] px-3 py-2.5 text-sm text-[#f3eefc] outline-none placeholder:text-[#7d6a9c] focus:border-[#a855f7]";

export const ClaimDialog = ({ open, onOpenChange, product, onClaimed }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: "", instagram: "", address: "", message: "", fulfillment: "Ship" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const f = (k, label, ph, required) => (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-[#a78bce]">{label}{required && " *"}</span>
      <input required={required} value={form[k]} placeholder={ph} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} data-testid={`claim-${k}-input`} />
    </label>
  );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/claims", { ...form, product_id: product.id });
      track("claim_submitted", { product_id: product.id });
      setDone(true);
      onClaimed?.();
      toast.success("Claim sent! We'll reach out shortly.");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (o) track("claim_started", { product_id: product?.id }); if (!o) setDone(false); }}>
      <DialogContent data-lenis-prevent className="max-h-[90vh] overflow-y-auto border-[#3b1d63] bg-[#0b0514] text-[#f3eefc] sm:max-w-lg" data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-normal">{done ? "It's yours to claim." : "Claim this piece"}</DialogTitle>
          <DialogDescription className="text-[#cbbbe6]">{done ? "We'll message you to confirm payment and shipping." : "No payment now — we'll contact you to finalise."}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-4 border-y border-[#281245] py-4">
          <img src={img(product?.images?.[0])} alt="" className="h-16 w-14 object-cover" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm">{product?.title}</p>
            <p className="font-mono-j text-xs text-[#a78bce]">{product?.size}</p>
          </div>
          <p className="font-mono-j">{money(product?.price)}</p>
        </div>
        {done ? (
          <div className="space-y-6 py-2" data-testid="claim-success">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-[#7c3aed]"><Check className="h-5 w-5" /></span>
            <Link to="/account" className="inline-block rounded-full bg-[#f3eefc] px-6 py-3 text-sm text-[#07030c]" data-testid="claim-view-claims-link">View my claims</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {f("name", "Full name", "", true)}
            <div className="grid gap-4 sm:grid-cols-2">
              {f("phone", "Phone", "(250) 555-0100")}
              {f("instagram", "Instagram / Depop", "@handle")}
            </div>
            {f("address", "Shipping address (Canada)", "Street, city, province, postal code", true)}
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-[#a78bce]">Message</span>
              <textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="claim-message-input" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[["Ship", "Ship across Canada"], ["Pickup", "Local pickup"]].map(([v, l]) => (
                <button type="button" key={v} onClick={() => setForm({ ...form, fulfillment: v })} data-testid={`claim-fulfillment-${v.toLowerCase()}`} className={`rounded-full border px-4 py-3 text-sm transition-colors duration-300 ${form.fulfillment === v ? "border-[#a855f7] bg-[#7c3aed] text-white" : "border-[#3b1d63] text-[#cbbbe6] hover:border-[#a855f7]"}`}>{l}</button>
              ))}
            </div>
            <button disabled={busy} className="w-full rounded-full bg-[#7c3aed] py-3.5 text-sm font-medium transition-colors hover:bg-[#a855f7] disabled:opacity-60" data-testid="claim-form-submit-button">
              {busy ? "Sending…" : `Claim for ${money(product?.price)}`}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
